import { useState } from "react";
import { ArrowUpRight, Car, Frame, Home, Trees } from "lucide-react";
import SeanceForm from "./SeanceForm";
import { MaskedLine, Reveal } from "./motion";

const PriceRow = ({ name, detail, price }) => (
    <div className="flex items-end justify-between gap-4 border-b border-dashed border-[#D4AF37]/20 pb-3">
        <div>
            <p className="text-sm font-normal text-white sm:text-base">
                {name}
            </p>
            {detail && (
                <p className="font-mono-lux mt-1.5 text-[9px] leading-relaxed tracking-[0.18em] text-[#8A8A8E]">
                    {detail}
                </p>
            )}
        </div>
        <span className="font-display text-gold-grad whitespace-nowrap text-xl font-semibold sm:text-2xl">
            {price}
        </span>
    </div>
);

const TarifCard = ({ icon: Icon, title, children, note, testId }) => (
    <div
        data-testid={testId}
        className="flex h-full flex-col gap-5 border border-[#D4AF37]/25 bg-[#0B0B0C] p-7 shadow-[0_20px_50px_rgba(0,0,0,0.8)] transition-colors duration-700 hover:border-[#D4AF37]/50 sm:p-9"
    >
        <div className="mb-2 flex items-center gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#D4AF37]/40 text-[#D4AF37]">
                <Icon size={18} strokeWidth={1.4} />
            </span>
            <h3 className="font-mono-lux text-xs tracking-[0.3em] text-[#D4AF37]">
                {title}
            </h3>
        </div>
        {children}
        {note && (
            <p className="mt-auto pt-3 text-xs font-light italic leading-relaxed text-[#8A8A8E]">
                {note}
            </p>
        )}
    </div>
);

export default function Tarifs() {
    const [formOpen, setFormOpen] = useState(false);
    return (
        <section
            id="tarifs"
            data-testid="section-tarifs"
            className="relative overflow-hidden border-t border-[#D4AF37]/15 py-24 sm:py-32"
        >
            <div className="pointer-events-none absolute left-1/2 top-0 h-[40vh] w-[70vw] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.07),transparent_65%)]" />

            <div className="relative z-10 mx-auto max-w-[1200px] px-5 sm:px-10">
                <Reveal>
                    <p className="font-mono-lux mb-6 flex items-center justify-center gap-4 text-[11px] tracking-[0.4em] text-[#D4AF37]">
                        <span className="inline-block h-px w-10 bg-[#D4AF37]/60" />
                        NOS PRIX
                        <span className="inline-block h-px w-10 bg-[#D4AF37]/60" />
                    </p>
                </Reveal>
                <h2
                    data-testid="tarifs-title"
                    className="font-display text-center text-3xl font-medium tracking-tight text-white sm:text-4xl lg:text-5xl"
                >
                    <MaskedLine delay={0.1}>
                        TARIFS <span className="text-[#8A8A8E]">&</span>{" "}
                        <span className="text-gold-grad italic">SÉANCES</span>
                    </MaskedLine>
                </h2>
                <Reveal delay={0.25}>
                    <p className="mx-auto mt-6 max-w-xl text-center text-base font-light leading-relaxed text-[#E2E2E2]/85 sm:text-lg">
                        Des séances adaptées à chaque moment, chaque histoire
                        et chaque personne.
                    </p>
                </Reveal>

                <div className="mt-16 grid gap-6 lg:grid-cols-2">
                    <Reveal delay={0.1} className="h-full">
                        <TarifCard
                            icon={Home}
                            title="SÉANCES EN STUDIO"
                            note="Personne supplémentaire : +20 $"
                            testId="tarifs-studio-card"
                        >
                            <PriceRow
                                name="Portrait individuel"
                                detail="ENVIRON 1 H • 10 PHOTOS RETOUCHÉES • GALERIE PRIVÉE"
                                price="125 $"
                            />
                            <PriceRow
                                name="Portrait couple"
                                detail="ENVIRON 1 H • 12 PHOTOS RETOUCHÉES • GALERIE PRIVÉE"
                                price="145 $"
                            />
                            <PriceRow
                                name="Portrait enfant"
                                detail="ENVIRON 1 H • 10 PHOTOS RETOUCHÉES • GALERIE PRIVÉE"
                                price="125 $"
                            />
                            <PriceRow
                                name="Portrait maternité"
                                detail="ENVIRON 1 H À 1 H 15 • 12 PHOTOS RETOUCHÉES • GALERIE PRIVÉE"
                                price="150 $"
                            />
                            <PriceRow
                                name="Portrait famille"
                                detail="ENVIRON 1 H À 1 H 30 • JUSQU'À 5 PERSONNES • 15 PHOTOS RETOUCHÉES • GALERIE PRIVÉE"
                                price="175 $"
                            />
                        </TarifCard>
                    </Reveal>

                    <Reveal delay={0.2} className="h-full">
                        <TarifCard
                            icon={Trees}
                            title="SÉANCES À L'EXTÉRIEUR"
                            note="Le tarif peut varier selon le nombre de personnes, d'animaux et les besoins de la séance."
                            testId="tarifs-exterieur-card"
                        >
                            <PriceRow
                                name="Séance extérieure"
                                detail="ENVIRON 1 H • 10 PHOTOS RETOUCHÉES • GALERIE PRIVÉE • LIEU CHOISI AVEC LE CLIENT"
                                price="DÈS 150 $"
                            />
                            <PriceRow
                                name="Animaux & chevaux"
                                detail="SÉANCE ADAPTÉE AUX ANIMAUX, CHEVAUX OU COMPAGNONS • PHOTOS RETOUCHÉES • GALERIE PRIVÉE"
                                price="DÈS 150 $"
                            />
                        </TarifCard>
                    </Reveal>

                    <Reveal delay={0.15} className="h-full">
                        <TarifCard
                            icon={Car}
                            title="FRAIS DE DÉPLACEMENT"
                            note="Les frais de déplacement sont calculés à partir de Saint-Jean-sur-Richelieu, Québec."
                            testId="tarifs-deplacement-card"
                        >
                            <PriceRow name="0 à 15 km" detail="" price="INCLUS" />
                            <PriceRow name="16 à 30 km" detail="" price="+15 $" />
                            <PriceRow name="31 à 50 km" detail="" price="+30 $" />
                            <PriceRow name="51 à 75 km" detail="" price="+45 $" />
                            <PriceRow
                                name="75 km et plus"
                                detail=""
                                price="SUR DEMANDE"
                            />
                        </TarifCard>
                    </Reveal>

                    <Reveal delay={0.25} className="h-full">
                        <TarifCard
                            icon={Frame}
                            title="IMPRESSIONS & AGRANDISSEMENTS"
                            testId="tarifs-impressions-card"
                        >
                            <p className="text-sm font-light leading-relaxed text-[#E2E2E2]/85 sm:text-base">
                                Formats d'impression, agrandissements et
                                encadrements disponibles sur demande.
                            </p>
                            <p className="text-sm font-light leading-relaxed text-[#E2E2E2]/85 sm:text-base">
                                Contactez-moi pour connaître les options et
                                les tarifs selon le format, l'impression et
                                l'encadrement désirés.
                            </p>
                        </TarifCard>
                    </Reveal>
                </div>

                <Reveal delay={0.3}>
                    <div className="mt-16 flex flex-col items-center gap-7 text-center">
                        <p className="font-mono-lux text-[11px] tracking-[0.4em] text-[#D4AF37]">
                            UNE SÉANCE VOUS INTÉRESSE ?
                        </p>
         <div className="w-full max-w-2xl border border-[#D4AF37]/50 bg-[#0B0B0C] p-6 sm:p-8 shadow-[0_18px_55px_rgba(212,175,55,0.16)]">
    <h3 className="font-display text-lg sm:text-xl text-gold-grad tracking-wide">
        🔒 GALERIE CLIENT PRIVÉE
    </h3>

    <p className="mt-4 text-sm leading-relaxed text-white sm:text-base">
        Vous avez fait votre séance avec Le Gars de la Photo?
        Retrouvez ici votre galerie privée pour consulter et télécharger
        vos photos en haute qualité.
    </p>

    <div className="mt-6 grid gap-5 sm:grid-cols-2 text-left">
        <div className="border-l-2 border-[#D4AF37] pl-4">
            <p className="text-sm font-semibold text-white">
                🔐 ACCÈS PRIVÉ ET PROTÉGÉ
            </p>
            <p className="mt-1 text-xs leading-relaxed text-[#A0A0A4]">
                Votre galerie est privée et protégée par un mot de passe remis après votre séance.
            </p>
        </div>

        <div className="border-l-2 border-[#D4AF37] pl-4">
            <p className="text-sm font-semibold text-white">
                📸 PHOTOS EN HAUTE QUALITÉ
            </p>
            <p className="mt-1 text-xs leading-relaxed text-[#A0A0A4]">
                Consultez vos photos et téléchargez-les en haute qualité.
            </p>
        </div>

        <div className="border-l-2 border-[#D4AF37] pl-4">
            <p className="text-sm font-semibold text-white">
                💻 ORDINATEUR OU TÉLÉPHONE
            </p>
            <p className="mt-1 text-xs leading-relaxed text-[#A0A0A4]">
                Accédez facilement à votre galerie depuis votre ordinateur ou votre téléphone.
            </p>
        </div>

        <div className="border-l-2 border-[#D4AF37] pl-4">
            <p className="text-sm font-semibold text-white">
                👤 POUR MES CLIENTS
            </p>
            <p className="mt-1 text-xs leading-relaxed text-[#A0A0A4]">
                Cette galerie est réservée aux clients ayant réalisé une séance avec Daniel.
            </p>
        </div>
        <div className="border-l-2 border-[#D4AF37] pl-4">
    <p className="text-sm font-semibold text-white">
        🔑 CLÉ USB — EN OPTION
    </p>
    <p className="mt-1 text-xs leading-relaxed text-[#A0A0A4]">
        Une clé USB contenant vos photos est disponible sur demande, avec un supplément.
    </p>
</div>
        <div className="border-l-2 border-[#D4AF37] pl-4">
    <p className="text-sm font-semibold text-white">
        🏠 LIVRAISON À DOMICILE
    </p>
    <p className="mt-1 text-xs leading-relaxed text-[#A0A0A4]">
        Livraison possible à domicile. Des frais peuvent s'appliquer selon la distance.
    </p>
</div>
    </div>

    <p className="mt-6 text-xs italic leading-relaxed text-[#A0A0A4]">
        Votre mot de passe vous est remis par Daniel après votre séance.
    </p>
</div>
           <a
    href="https://photographedanieldulude.gallery.aftershoot.com/collection/le-gars-de-la-photo"
    target="_blank"
    rel="noopener noreferrer"
    className="group inline-flex items-center gap-4 border border-[#D4AF37] bg-gradient-to-r from-[#9A7B38] via-[#D4AF37] to-[#9A7B38] px-8 py-4 font-mono-lux text-[10px] tracking-[0.25em] text-[#050505] transition-all duration-500 hover:shadow-[0_0_45px_rgba(212,175,55,0.35)] sm:px-12 sm:py-5 sm:text-xs sm:tracking-[0.3em]"
>
    ACCÉDER À MA GALERIE
    <ArrowUpRight
        size={17}
        strokeWidth={1.6}
        className="transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
    />
</a>             
                        <button
                            type="button"
                            data-testid="demander-seance-button"
                            onClick={() => setFormOpen(true)}
                            className="group inline-flex items-center gap-4 border border-[#D4AF37] bg-gradient-to-r from-[#9A7B38] via-[#D4AF37] to-[#9A7B38] px-8 py-4 font-mono-lux text-[10px] tracking-[0.25em] text-[#050505] transition-all duration-500 hover:shadow-[0_0_45px_rgba(212,175,55,0.35)] sm:px-12 sm:py-5 sm:text-xs sm:tracking-[0.3em]"
                        >
                            DEMANDER UNE SÉANCE
                            <ArrowUpRight
                                size={17}
                                strokeWidth={1.6}
                                className="transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                            />
                        </button>
                    </div>
                </Reveal>
            </div>
            <SeanceForm open={formOpen} onClose={() => setFormOpen(false)} />
        </section>
    );
}
